// FPApp.js
// Franchise Player — app shell (header + routes)
// Mirrors the UEApp.js pattern: game header, nested routes, admin-only extras.

import React, { useState } from "react";
import { Routes, Route, useNavigate, useLocation } from "react-router-dom";
import spaceNeedle from "./Space Needle.jpg";
import { useAuth } from "./App";
import FPLeagueHome from "./FPLeagueHome";
import FPLeagueDetail from "./FPLeagueDetail";
import FPAdminPanel from "./FPAdminPanel";
import { FP_ACCENT, FP_ACCENT_LIGHT, FP_ACCENT_DARK } from "./fp-theme";

const FP_ADMIN_UID = "fy1HRZav9TW5MJFxHE1hc3mJFUl2"; // same platform admin as WC Pool / Underdog Edge

// Captain's armband: the Franchise Player mark
export function CaptainIcon({ size = 24 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 100 100" style={{ flexShrink: 0 }}>
      <rect x="8" y="26" width="84" height="48" rx="10" fill={FP_ACCENT} stroke={FP_ACCENT_DARK} strokeWidth="4" />
      <rect x="8" y="34" width="84" height="6" fill="rgba(255,255,255,0.25)" />
      <rect x="8" y="60" width="84" height="6" fill="rgba(255,255,255,0.25)" />
      <text x="50" y="64" textAnchor="middle" fontFamily="'Bebas Neue', sans-serif" fontSize="42" fill="white">C</text>
    </svg>
  );
}

function FPHeader({ activeLeagueName }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, displayName, logout } = useAuth();
  const inLeague = location.pathname.includes("/franchise-player/league/");

  return (
    <header style={{
      background: "linear-gradient(135deg, #0a1220 0%, #0f1f2a 50%, #0a1220 100%)",
      borderBottom: `3px solid ${FP_ACCENT}`,
      position: "relative",
      overflow: "hidden",
    }}>
      {/* Yard-line pattern */}
      <div style={{
        position: "absolute", inset: 0, opacity: 0.035,
        backgroundImage: "repeating-linear-gradient(90deg, white 0px, white 2px, transparent 2px, transparent 40px), repeating-linear-gradient(0deg, white 0px, white 1px, transparent 1px, transparent 20px)",
        pointerEvents: "none",
      }} />

      <div style={{ height: 4, background: `linear-gradient(90deg, ${FP_ACCENT}, ${FP_ACCENT_LIGHT}, ${FP_ACCENT}, ${FP_ACCENT_DARK})` }} />

      <div style={{ maxWidth: 1000, margin: "0 auto", padding: "6px 12px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <button onClick={() => navigate("/")} style={{ background: "none", border: "none", cursor: "pointer", padding: 4, flexShrink: 0, display: "flex", alignItems: "center" }} title="Home">
            <img src={spaceNeedle} alt="Home" style={{ width: 32, height: 32, borderRadius: 6, objectFit: "cover" }} />
          </button>

          <div
            onClick={() => navigate("/franchise-player")}
            style={{ display: "flex", alignItems: "center", gap: 8, flex: 1, minWidth: 0, cursor: "pointer" }}
          >
            <CaptainIcon size={28} />
            <div style={{ minWidth: 0 }}>
              <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(14px, 4vw, 18px)", letterSpacing: 2, color: "white", lineHeight: 1, whiteSpace: "nowrap" }}>
                FRANCHISE <span style={{ color: FP_ACCENT }}>PLAYER</span>
              </div>
              {inLeague && activeLeagueName && (
                <div style={{ fontSize: 10, color: "rgba(255,255,255,0.35)", letterSpacing: 0.5, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {activeLeagueName}
                </div>
              )}
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }} className="hide-mobile">
            {user && (
              <>
                <button onClick={() => navigate("/account")} className="btn btn-ghost btn-sm">👤 {displayName}</button>
                <button onClick={logout} className="btn btn-ghost btn-sm">Sign Out</button>
              </>
            )}
          </div>

          <div className="hide-desktop" style={{ flexShrink: 0 }}>
            {user && <button onClick={() => navigate("/account")} className="btn btn-ghost btn-sm">👤</button>}
          </div>
        </div>
      </div>

      <div style={{ height: 3, background: `linear-gradient(90deg, ${FP_ACCENT_DARK}, ${FP_ACCENT}, ${FP_ACCENT_LIGHT}, ${FP_ACCENT_DARK})` }} />
    </header>
  );
}

export default function FPApp() {
  const { user } = useAuth();
  const [activeLeagueName, setActiveLeagueName] = useState(null);

  return (
    <div style={{ minHeight: "100vh" }}>
      <FPHeader activeLeagueName={activeLeagueName} />

      <div style={{ maxWidth: 1000, margin: "0 auto", padding: "24px 16px" }}>
        <Routes>
          <Route path="" element={<FPLeagueHome />} />
          <Route path="league/:leagueId" element={
            <FPLeagueDetail
              onMount={(name) => setActiveLeagueName(name)}
              onLeave={() => setActiveLeagueName(null)}
            />
          } />
        </Routes>

        {/* Admin tools — rendered below any Franchise Player screen for the admin */}
        {user?.uid === FP_ADMIN_UID && <FPAdminPanel />}
      </div>
    </div>
  );
}
